import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Divider,
    IconButton,
    Tooltip,
    useTheme,
    Dialog,
    DialogContent,
    DialogContentText,
    DialogTitle,
    DialogActions,
    FormControlLabel,
    Switch,
    TextField,
    Typography,
} from '@material-ui/core'
import FileCopyIcon from '@material-ui/icons/FileCopy'

import { Pagination } from '@material-ui/lab'
import RefreshIcon from '@material-ui/icons/Refresh'
import TuneIcon from '@material-ui/icons/Tune'
import CloseIcon from '@material-ui/icons/Close'
import SelectAllIcon from '@material-ui/icons/SelectAll'
import { LoriIcon } from '../../navigation/layouts/Lori/Icons/LoriIcon'
import { LoriActionsModal } from './LoriActionsModal'
import { DuplicateBlanket } from './DuplicateBlanket'

import {
    Container,
    DamageDrawer,
    MessageDrawer,
    ScheduleReportPopOver,
} from '../../components'
import {
    DMG_RESET_TO_PENDING,
    Damage,
    DamageConfig,
    DamageStatus,
    ModelListMap,
    ModelMap,
    Unit,
    traverse,
} from '../../models'
import { DamageRow } from './DamageRow'
import { DamageStatusChangeButtons } from './DamageStatusChangeButtons'
import { toast } from 'react-toastify'
import {
    FinderSelection,
    useAptConfig,
    _useFinderSelection,
    useMessageDrawer,
    usePaginatedDamageList,
    useRootInfrastructure,
    useUser,
    useAppDispatch,
    DamageRequestFilters,
    _useLease,
} from '../../hooks'
import { DamageFilterDrawer } from './DamageFilterDrawer'
import {
    FinderLocationSelection,
    FinderSelectionMode,
    RootState,
    getInspectionList,
} from '../../store'
import { DamageFilterChips } from './DamageFilterChips'
import { TenantPopover } from './TenantPopover'
import {
    axiosInstance,
    currencyFormatter,
    damageCostDeltaForStatusChange,
    isAccountManager,
} from '../../helpers'
import { useSelector } from 'react-redux'
import { fade } from '@material-ui/core/styles/colorManipulator'
import { typographyStyle } from '../../theme/typographyStyle'
import { hasPermission } from '../../models/Users/services'

const PAGE_SIZE = 25

/** Client-side filter for “Find transaction issues” — must match `filteredDamages` logic */
function damagePassesArTransactionFilter(
    dmg: Damage,
    arTransactionErrorMode: boolean,
    today: Date,
    endCutoff: Date,
): boolean {
    if (!arTransactionErrorMode) {
        return true
    }
    if (dmg.damage_transactions.length === 0) {
        return true
    }
    let hasIssue = false
    dmg.damage_transactions.forEach((arTransaction) => {
        const startDate = new Date(arTransaction.lease.start_date)
        const endDate = new Date(arTransaction.lease.end_date)
        if (startDate > today) {
            hasIssue = true
        }
        if (endDate < endCutoff) {
            hasIssue = true
        }
    })
    return hasIssue
}

/** When select-all used id-only fetch, bulk helpers need a row shape; status inferred from single-status filter when possible */
function stubDamageForBulkSelect(
    id: number,
    inferredStatus: DamageStatus,
): Damage {
    return ({
        id,
        status: inferredStatus,
        damage_transactions: [],
        amount: 0,
        cost: 0,
    } as unknown) as Damage
}

export type DamageDateStatusFilterSnapshot = {
    created_after: Date
    created_before: Date
    status: DamageStatus[]
}

export type DamageDateStatusFilterApply = (
    created_after: Date,
    created_before: Date,
    status: DamageStatus[],
) => void

interface Props {
    damageConfigList?: DamageConfig[]
    /** When set (e.g. EZFolio), called when filters/finder scope changes — not on page-only changes or property/workspace switches */
    onListFilterParamsChange?: (params: DamageRequestFilters) => void
    /** EZFolio: adjust portfolio KPIs locally after a status change (no API) */
    onDamageStatusAdjusted?: (
        damages: Damage[],
        newStatus: DamageStatus,
    ) => void
    /** EZFolio: keep parent/modal in sync with date + status filter values */
    onDateStatusFilterSnapshot?: (
        snapshot: DamageDateStatusFilterSnapshot,
    ) => void
    /** EZFolio: parent/modal applies date + status (same state as the list) */
    dateStatusFilterApplyRef?: React.MutableRefObject<DamageDateStatusFilterApply | null>
}

export enum HasIssuesFilter {
    ISSUES = 'ISSUES',
    NO_ISSUES = 'NO_ISSUES',
    NO_FILTER = 'NO_FILTER',
}

export type HasIssuesFilterType = `${HasIssuesFilter}`
export interface DamageFilter {
    status: DamageStatus[]
    modified: boolean
    hasIssues: HasIssuesFilterType
    inspection: number
    created_date_sort: boolean
    created_before: Date
    created_after: Date
    search: string
    hasAttachment: boolean
    hasMatchingIssue: boolean
    costFilter: {
        cost: number | null
        operator: 'gt' | 'lt' | 'eq' | 'gte' | 'lte'
    }
}

const oneYearAgo = new Date()
oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1)

const oneDayFromNow = new Date()
oneDayFromNow.setDate(oneDayFromNow.getDate() + 1)

export const defaultDamageFilter: DamageFilter = {
    status: [DamageStatus.PENDING],
    modified: false,
    hasIssues: HasIssuesFilter.NO_FILTER,
    inspection: -1,
    created_date_sort: false,
    created_before: oneDayFromNow,
    created_after: oneYearAgo,
    search: '',
    hasAttachment: false,
    hasMatchingIssue: false,
    costFilter: {
        cost: null,
        operator: 'gt',
    },
}

export const DamageList = (props: Props) => {
    const {
        damageConfigList,
        onListFilterParamsChange,
        onDamageStatusAdjusted,
        onDateStatusFilterSnapshot,
        dateStatusFilterApplyRef,
    } = props
    const dispatch = useAppDispatch()

    const [damageFilterState, setDamageFilterState] = useState(
        defaultDamageFilter,
    )
    const [damageCost, setDamageCost] = useState(0)
    const [selectedState, setSelectedState] = useState<boolean[]>([])
    const [selectAllAcrossPages, setSelectAllAcrossPages] = useState(false)
    const [allSelectedDamageIds, setAllSelectedDamageIds] = useState<number[]>(
        [],
    )

    const inspectionList = useSelector(
        (state: RootState) => state.inspection.inspectionList,
    )

    const appliedFinderSelection = _useFinderSelection({
        whichSelection: FinderLocationSelection.PrunedSelection,
        selectionMode: FinderSelectionMode.Recursive,
    })

    const [
        tenantPopoverAnchorEl,
        setTenantPopoverAnchorEl,
    ] = useState<HTMLElement | null>(null)
    const [
        reportPopoverAnchorEl,
        setReportPopoverAnchorEl,
    ] = useState<HTMLElement | null>(null)

    const [selectedDamage, setSelectedDamage] = useState<Damage | null>(null)
    const [unitDamagesLoading, setUnitDamagesLoading] = useState(false)

    const openTenantPopover = useCallback(
        (event: React.MouseEvent<HTMLElement>, damageId: number) => {
            setSelectedDamageId(damageId)
            setTenantPopoverAnchorEl(event.currentTarget)
        },
        [],
    )

    const closeTenantPopover = useCallback(() => {
        setTenantPopoverAnchorEl(null)
    }, [])

    const openReportPopover = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            setReportPopoverAnchorEl(event.currentTarget)
        },
        [],
    )

    const closeReportPopover = useCallback(() => {
        setReportPopoverAnchorEl(null)
    }, [])

    const reportPopoverOpen = Boolean(reportPopoverAnchorEl)
    const tenantPopOverOpen = Boolean(tenantPopoverAnchorEl)

    const {
        damageList,
        getDamageList,
        loadingDamages,
        setPage,
        currentPage,
        totalDamageCount,
        updateDamageStatus,
        removeDamageTransaction,
        addDamageTransaction,
        updateDamageState,
        updateDamage,
        statusCounts,
        getDamageStatusCounts,
        removeAllPastTransactions,
        matchingDamageIds,
    } = usePaginatedDamageList(
        PAGE_SIZE,
        (res, damageList) => {
            setSelectedState(damageList?.map(() => false) ?? [])
            setSelectAllAcrossPages(false)
            setAllSelectedDamageIds([])
        },
        (e) => toast.error(e.message),
    )

    const {
        damageList: simpleDamageList,
        getDamageList: getDamageListForInspectionAndUnit,
        createDamage,
    } = _useLease({
        cleanUp: true,
    })

    const { tree } = useRootInfrastructure(true)

    const [openMessageDamageId, setOpenMessageDamageId] = useState(-1)
    const [filterOpen, setFilterOpen] = useState(false)
    const [selectedDamageId, setSelectedDamageId] = useState<number>(-1)
    const [dialogOpen, setDialogOpen] = useState(false)
    const [
        deletePastTransactionsDialogOpen,
        setDeletePastTransactionsDialogOpen,
    ] = useState(false)

    const [arTransactionErrorMode, setArTransactionErrorMode] = useState(false)
    const [loriModalOpen, setLoriModalOpen] = useState(false)
    const [matchingTenantIssues, setMatchingTenantIssues] = useState(false)
    const [markingDuplicates, setMarkingDuplicates] = useState(false)
    const [duplicateResults, setDuplicateResults] = useState<{
        strict: Array<{ original: Damage; duplicates: Damage[] }>
        possible: Array<{ original: Damage; duplicates: Damage[] }>
    }>({ strict: [], possible: [] })
    const [duplicateBlanketOpen, setDuplicateBlanketOpen] = useState(false)

    const { workspaceUser } = useUser()
    const damageFilterStateRef = useRef(damageFilterState)
    damageFilterStateRef.current = damageFilterState
    /** Tracks last workspace id so we skip portfolio parent notify on property switches (still notify on first load). */
    const portfolioNotifyPrevWorkspaceIdRef = useRef<number | undefined>(
        undefined,
    )
    const { setChannel } = useMessageDrawer()

    const theme = useTheme()

    const onOpenMessageDrawer = (channelId: number, damageId: number) => {
        setChannel(channelId)
        setOpenMessageDamageId(damageId)
    }

    const handleSearchChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setDamageFilterState({
                ...damageFilterState,
                search: event.target.value,
                modified: true,
            })
        },
        [],
    )

    const getFilterParams = (
        filter: DamageFilter,
        finderSelection?: FinderSelection,
    ): DamageRequestFilters => {
        let unitIdString: string | undefined

        if (finderSelection) {
            unitIdString = ''
            Object.keys(finderSelection.selection.unit).forEach((unitKey) => {
                // Exclude the "length" property
                if (unitKey === 'length') {
                    return
                }

                // add the unit id if it is selected in the finder
                const unitKeyNumber = parseInt(unitKey)
                if (
                    !isNaN(unitKeyNumber) &&
                    finderSelection.selection.unit[unitKeyNumber]
                ) {
                    unitIdString += `${unitKey},`
                }
            })

            // Remove the trailing comma
            if (unitIdString.length > 0) {
                unitIdString = unitIdString.substring(
                    0,
                    unitIdString.length - 1,
                )
            }
        }

        if (unitIdString === '') {
            unitIdString = undefined
        }

        let statusString: string | undefined
        if (filter.status.length > 0) {
            statusString = ''
            filter.status.forEach((status) => {
                statusString += `${status},`
            })
            if (statusString.length > 0) {
                statusString = statusString.substring(
                    0,
                    statusString.length - 1,
                )
            }
        }

        let issues: boolean | undefined
        if (filter.hasIssues === HasIssuesFilter.ISSUES) {
            issues = true
        }
        if (filter.hasIssues === HasIssuesFilter.NO_ISSUES) {
            issues = false
        }

        let inspection: number | undefined
        if (filter.inspection !== -1) {
            inspection = filter.inspection
        }

        let sort_by_created_date: boolean | undefined
        if (filter.created_date_sort) {
            sort_by_created_date = true
        }

        let search: string | undefined
        if (filter.search.length > 0) {
            search = filter.search.toLocaleLowerCase()
        }

        let hasAttachment: boolean | undefined
        if (filter.hasAttachment) {
            hasAttachment = true
        }

        let hasMatchingIssue: boolean | undefined
        if (filter.hasMatchingIssue) {
            hasMatchingIssue = true
        }

        let cost: number | undefined
        let cost_operator: string | undefined
        if (filter.costFilter.cost !== null) {
            cost = filter.costFilter.cost
            cost_operator = filter.costFilter.operator
        }

        return {
            excludeDenied: true,
            unitIds: unitIdString,
            statuses: statusString,
            issues: issues,
            inspection: inspection,
            sort_by_created_date: sort_by_created_date,
            created_before: filter.created_before.toISOString(),
            created_after: filter.created_after.toISOString(),
            search: search,
            hasAttachment: hasAttachment,
            hasMatchingIssue: hasMatchingIssue,
            cost: cost,
            cost_operator: cost_operator,
        }
    }

    const getFilteredDamages = (
        filter: DamageFilter,
        page: number,
        finderSelection?: FinderSelection,
        notifyPortfolioFilterParams?: boolean,
    ) => {
        const params = getFilterParams(filter, finderSelection)
        axiosInstance
            .get('lease/damage/get_total_cost/', { params: params })
            .then((res) => {
                setDamageCost(res.data)
            })

        getDamageList(page, params)
        getDamageStatusCounts(params)

        if (notifyPortfolioFilterParams && onListFilterParamsChange) {
            onListFilterParamsChange(params)
        }
    }

    const handleDamageStatusChange = useCallback(
        (damages: Damage[], status: DamageStatus) => {
            const wasSelectAll = selectAllAcrossPages
            const result = updateDamageStatus(damages, status, {
                skipApprovedTransactionCheck: wasSelectAll,
                skipOptimisticCounts: wasSelectAll,
            })
            if (result) {
                result.then(() => {
                    setSelectAllAcrossPages(false)
                    setAllSelectedDamageIds([])
                    const params = getFilterParams(
                        damageFilterStateRef.current,
                        appliedFinderSelection,
                    )
                    if (wasSelectAll) {
                        axiosInstance
                            .get('lease/damage/get_total_cost/', {
                                params: params,
                            })
                            .then((res) => {
                                setDamageCost(res.data)
                            })
                        void getDamageStatusCounts(params)
                        onListFilterParamsChange?.(params)
                    } else {
                        const costDelta = damageCostDeltaForStatusChange(
                            damages,
                            status,
                            params,
                        )
                        setDamageCost((prev) => {
                            const n = typeof prev === 'number' ? prev : 0
                            return n + costDelta
                        })
                        onDamageStatusAdjusted?.(damages, status)
                    }
                })
            }
            return result
        },
        [
            updateDamageStatus,
            appliedFinderSelection,
            onDamageStatusAdjusted,
            onListFilterParamsChange,
            getDamageStatusCounts,
            selectAllAcrossPages,
        ],
    )

    const applyFilter = (
        filter: DamageFilter,
        finderSelection?: FinderSelection,
        page?: number,
    ) => {
        setSelectAllAcrossPages(false)
        setAllSelectedDamageIds([])
        // finderSelection should be undefined when clearing the filter
        if (finderSelection === undefined) {
            appliedFinderSelection.setFinderSelection(undefined)
        }

        // reset the page back to 1
        let newPage = 1
        if (page !== undefined) {
            newPage = page
        }
        setPage(newPage)

        // Make network request (notify parent for EZFolio portfolio analytics)
        getFilteredDamages(filter, newPage, finderSelection, true)

        // make sure the modification state is set to false
        setDamageFilterState({ ...filter, modified: false })

        // close the filter drawer
        setFilterOpen(false)
    }

    const applyDateAndStatusFilter = (
        created_after: Date,
        created_before: Date,
        status: DamageStatus[],
    ) => {
        applyFilter(
            {
                ...damageFilterStateRef.current,
                created_after,
                created_before,
                status,
            },
            appliedFinderSelection,
        )
    }

    if (dateStatusFilterApplyRef) {
        dateStatusFilterApplyRef.current = applyDateAndStatusFilter
    }

    useEffect(() => {
        onDateStatusFilterSnapshot?.({
            created_after: damageFilterState.created_after,
            created_before: damageFilterState.created_before,
            status: damageFilterState.status,
        })
    }, [
        damageFilterState.created_after,
        damageFilterState.created_before,
        damageFilterState.status,
        onDateStatusFilterSnapshot,
    ])

    useEffect(() => {
        return () => {
            if (dateStatusFilterApplyRef) {
                dateStatusFilterApplyRef.current = null
            }
        }
    }, [dateStatusFilterApplyRef])

    const handleMarkDuplicates = () => {
        const params = getFilterParams(
            damageFilterState,
            appliedFinderSelection,
        )
        setDuplicateResults({ strict: [], possible: [] })
        setDuplicateBlanketOpen(true)
        setMarkingDuplicates(true)
        axiosInstance
            .post('lease/damage/mark_duplicates/', null, { params })
            .then((res) => {
                setDuplicateResults({
                    strict: res.data?.strict ?? [],
                    possible: res.data?.possible ?? [],
                })
            })
            .catch((e) => {
                toast.error(`Failed to mark duplicates: ${e.message}`)
                setDuplicateBlanketOpen(false)
            })
            .finally(() => setMarkingDuplicates(false))
    }

    const handleMatchTenantIssues = () => {
        const params = getFilterParams(
            damageFilterState,
            appliedFinderSelection,
        )
        setMatchingTenantIssues(true)
        axiosInstance
            .post('lease/damage/match_tenant_issues/', null, { params })
            .then(() => {
                toast.success(
                    'Tenant issue matching is processing — you will receive an email when complete.',
                    { autoClose: 8000 },
                )
                applyFilter(
                    damageFilterState,
                    appliedFinderSelection,
                    currentPage,
                )
            })
            .catch((e) => {
                toast.error(`Failed to match tenant issues: ${e.message}`)
            })
            .finally(() => setMatchingTenantIssues(false))
    }

    const handleCloseDuplicateBlanket = () => {
        setDuplicateBlanketOpen(false)
        applyFilter(damageFilterState, appliedFinderSelection, currentPage)
    }

    const filteredDamages = useMemo(() => {
        const today = new Date()
        const endCutoff = new Date()
        endCutoff.setDate(endCutoff.getDate() - 122)
        return (
            damageList?.filter((dmg) =>
                damagePassesArTransactionFilter(
                    dmg,
                    arTransactionErrorMode,
                    today,
                    endCutoff,
                ),
            ) ?? []
        )
    }, [damageList, arTransactionErrorMode])

    /**
     * Status used for select-all-across-pages stubs so bulk transition buttons match
     * the header checkbox (all rows on this page selected): same as every visible row
     * when they share one status; otherwise null and we fall back to per-id rows.
     */
    const selectAllBulkStubStatus = useMemo((): DamageStatus | null => {
        if (filteredDamages.length === 0) {
            return damageFilterState.status.length === 1
                ? damageFilterState.status[0]
                : DamageStatus.PENDING
        }
        const first = filteredDamages[0].status
        return filteredDamages.every((d) => d.status === first) ? first : null
    }, [filteredDamages, damageFilterState.status])

    const damagesForBulk = useMemo(() => {
        if (selectAllAcrossPages && allSelectedDamageIds.length > 0) {
            if (selectAllBulkStubStatus !== null) {
                return allSelectedDamageIds.map((id) =>
                    stubDamageForBulkSelect(id, selectAllBulkStubStatus),
                )
            }
            const inferredStatus =
                damageFilterState.status.length === 1
                    ? damageFilterState.status[0]
                    : DamageStatus.PENDING
            return allSelectedDamageIds.map((id) => {
                const row = damageList?.find((d) => d.id === id)
                return row ?? stubDamageForBulkSelect(id, inferredStatus)
            })
        }
        return filteredDamages.filter((_, idx) => selectedState[idx])
    }, [
        selectAllAcrossPages,
        allSelectedDamageIds,
        selectAllBulkStubStatus,
        damageFilterState.status,
        damageList,
        filteredDamages,
        selectedState,
    ])

    const selectedCount = useMemo(() => {
        if (selectAllAcrossPages && allSelectedDamageIds.length > 0) {
            return allSelectedDamageIds.length
        }
        return selectedState.reduce((n, s) => n + (s ? 1 : 0), 0)
    }, [selectAllAcrossPages, allSelectedDamageIds, selectedState])

    const allOnPageSelected =
        selectedState.length > 0 && selectedState.every(Boolean)

    const showSelectAllAcrossPages =
        !selectAllAcrossPages &&
        allOnPageSelected &&
        matchingDamageIds.length > filteredDamages.length

    const { isChecked, intermediate } = useMemo(() => {
        if (selectAllAcrossPages) {
            return { isChecked: true, intermediate: false }
        }
        if (selectedState.length === 0) {
            return { isChecked: false, intermediate: false }
        }
        const some = selectedState.some(Boolean)
        const every = selectedState.every(Boolean)
        return {
            isChecked: every,
            intermediate: some && !every,
        }
    }, [selectAllAcrossPages, selectedState])

    const handleSelectAllAcrossPages = useCallback(() => {
        if (arTransactionErrorMode) {
            toast.error(
                'Select all is unavailable while using Find transaction issues — turn it off first.',
            )
            return
        }
        if (matchingDamageIds.length === 0) {
            toast.error('No damages match the current filters')
            return
        }
        setAllSelectedDamageIds(matchingDamageIds)
        setSelectAllAcrossPages(true)
        setSelectedState(filteredDamages.map(() => false))
    }, [arTransactionErrorMode, matchingDamageIds, filteredDamages])

    const {
        getInventoryConfigMap,
        getAreaConfigMap,
        refreshConfigs: refreshAptConfigs,
    } = useAptConfig({
        inventoryConfigList: true,
        areaConfigList: true,
    })

    const inventoryConfigMap = getInventoryConfigMap()
    const areaConfigMap = getAreaConfigMap()

    const [unitMap, setUnitMap] = useState<ModelMap<Unit>>({})

    useEffect(() => {
        const newUnitMap: ModelMap<Unit> = {}

        traverse(tree.root, (folder) => {
            folder.units.forEach((u) => {
                newUnitMap[u.id] = u
            })
        })
        setUnitMap(newUnitMap)
    }, [tree.version])

    const unitDamageMap = useMemo(() => {
        const newUnitDamageMap: ModelListMap<Damage> = {}
        simpleDamageList?.forEach((dmg) => {
            newUnitDamageMap[dmg.unit] = [
                ...(newUnitDamageMap[dmg.unit] ?? []),
                dmg,
            ]
        })
        return newUnitDamageMap
    }, [simpleDamageList])

    useEffect(() => {
        if (!workspaceUser?.active_workspace?.id) return

        setSelectAllAcrossPages(false)
        setAllSelectedDamageIds([])

        const wsId = workspaceUser.active_workspace.id
        const isPropertySwitch =
            portfolioNotifyPrevWorkspaceIdRef.current !== undefined &&
            portfolioNotifyPrevWorkspaceIdRef.current !== wsId
        portfolioNotifyPrevWorkspaceIdRef.current = wsId

        dispatch(getInspectionList({ params: {} }))
        appliedFinderSelection.setFinderSelection(undefined)
        setPage(1)
        void refreshAptConfigs()
        getFilteredDamages(
            damageFilterStateRef.current,
            1,
            undefined,
            !isPropertySwitch,
        )
    }, [workspaceUser?.active_workspace?.id])

    return (
        <>
            <div
                style={{
                    position: 'relative',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    minHeight: 0,
                }}
            >
                <Container
                    style={{
                        flexDirection: 'column',
                        flex: 1,
                    }}
                    scrollY
                >
                    <Container
                        style={{
                            flex: 1,
                            flexDirection: 'column',
                            padding: theme.spacing(1),
                        }}
                        scrollY
                    >
                        <Container
                            style={{
                                alignItems: 'center',
                                display: 'flex',
                                flexWrap: 'wrap',
                            }}
                        >
                            <Checkbox
                                checked={isChecked === true}
                                onChange={() => {
                                    if (selectAllAcrossPages) {
                                        setSelectAllAcrossPages(false)
                                        setAllSelectedDamageIds([])
                                        setSelectedState(
                                            selectedState.map(() => false),
                                        )
                                        return
                                    }
                                    setSelectedState(
                                        selectedState.map(() => !isChecked),
                                    )
                                }}
                                indeterminate={intermediate}
                            />

                            <span
                                style={{
                                    ...typographyStyle(theme.typography.body1),
                                    marginLeft: theme.spacing(1),
                                    marginRight: theme.spacing(1),
                                }}
                            >
                                {selectedCount > 0
                                    ? `${selectedCount} Selected`
                                    : ''}
                            </span>

                            {showSelectAllAcrossPages && (
                                <Box
                                    component="span"
                                    display="inline-flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    style={{
                                        marginRight: theme.spacing(1.5),
                                        borderRadius: 8,
                                        border: `1px solid ${fade(
                                            theme.palette.primary.main,
                                            0.35,
                                        )}`,
                                        backgroundColor: fade(
                                            theme.palette.primary.main,
                                            0.08,
                                        ),
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                                        padding: theme.spacing(0.25, 0.5),
                                    }}
                                >
                                    <Button
                                        color="primary"
                                        size="small"
                                        variant="text"
                                        disabled={loadingDamages}
                                        onClick={handleSelectAllAcrossPages}
                                        startIcon={
                                            <SelectAllIcon
                                                style={{
                                                    fontSize: 18,
                                                    display: 'flex',
                                                }}
                                            />
                                        }
                                        style={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            letterSpacing: '0.01em',
                                            padding: theme.spacing(0.75, 1.5),
                                            borderRadius: 7,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            minHeight: 36,
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {`Select all ${matchingDamageIds.length}`}
                                    </Button>
                                </Box>
                            )}

                            {selectAllAcrossPages && (
                                <Box
                                    component="span"
                                    display="inline-flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    style={{
                                        marginRight: theme.spacing(1.5),
                                        borderRadius: 8,
                                        border: `1px solid ${theme.palette.divider}`,
                                        backgroundColor:
                                            theme.palette.grey[100],
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                                        padding: theme.spacing(0.25, 0.5),
                                    }}
                                >
                                    <Button
                                        size="small"
                                        variant="text"
                                        onClick={() => {
                                            setSelectAllAcrossPages(false)
                                            setAllSelectedDamageIds([])
                                            setSelectedState(
                                                selectedState.map(() => false),
                                            )
                                        }}
                                        startIcon={
                                            <CloseIcon
                                                style={{
                                                    fontSize: 18,
                                                    opacity: 0.85,
                                                    display: 'flex',
                                                }}
                                            />
                                        }
                                        style={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            letterSpacing: '0.01em',
                                            color: theme.palette.text.secondary,
                                            padding: theme.spacing(0.75, 1.5),
                                            borderRadius: 7,
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            minHeight: 36,
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        Clear selection
                                    </Button>
                                </Box>
                            )}

                            {selectedCount > 0 && (
                                <DamageStatusChangeButtons
                                    damages={damagesForBulk}
                                    transitionDamage={
                                        selectAllAcrossPages &&
                                        selectAllBulkStubStatus !== null &&
                                        filteredDamages[0] !== undefined
                                            ? filteredDamages[0]
                                            : undefined
                                    }
                                    handleStatusChange={
                                        handleDamageStatusChange
                                    }
                                    workspaceUser={workspaceUser}
                                />
                            )}

                            {hasPermission(
                                workspaceUser,
                                DMG_RESET_TO_PENDING,
                            ) &&
                                selectedCount > 0 && (
                                    <Button
                                        color="secondary"
                                        variant="outlined"
                                        style={{
                                            marginRight: theme.spacing(1),
                                        }}
                                        onClick={() => {
                                            setDialogOpen(true)
                                        }}
                                    >
                                        Reset to Pending
                                    </Button>
                                )}

                            <TextField
                                label="Search"
                                variant="outlined"
                                value={damageFilterState.search}
                                onChange={handleSearchChange}
                                style={{
                                    marginRight: theme.spacing(2),
                                }}
                                size="small"
                            />

                            <Button
                                variant="outlined"
                                onClick={() =>
                                    setDeletePastTransactionsDialogOpen(true)
                                }
                                size="small"
                                style={{
                                    textTransform: 'none',
                                    backgroundColor: 'white',
                                    color: 'red',
                                    marginRight: theme.spacing(1),
                                }}
                            >
                                Cancel Past Transactions
                            </Button>

                            {damageFilterState.modified && (
                                <Button
                                    color="primary"
                                    variant="outlined"
                                    onClick={() =>
                                        applyFilter(
                                            damageFilterState,
                                            appliedFinderSelection,
                                        )
                                    }
                                    style={{ marginRight: theme.spacing(1) }}
                                >
                                    Apply Filter Changes
                                </Button>
                            )}

                            <DamageFilterChips
                                damageFilter={damageFilterState}
                                finderSelection={appliedFinderSelection}
                                inspectionList={inspectionList ?? []}
                                setDamageFilter={setDamageFilterState}
                                onCommitDateStatusFilter={
                                    applyDateAndStatusFilter
                                }
                                statusCounts={statusCounts}
                            />

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={arTransactionErrorMode}
                                        color="primary"
                                        onChange={() => {
                                            setSelectAllAcrossPages(false)
                                            setAllSelectedDamageIds([])
                                            setSelectedState((oldState) => {
                                                return oldState.map(() => false)
                                            })
                                            setArTransactionErrorMode((v) => !v)
                                        }}
                                    />
                                }
                                labelPlacement="top"
                                label="Find transaction issues"
                            />

                            <div style={{ flex: 1, minWidth: 8 }} />
                            <Container
                                style={{
                                    alignItems: 'center',
                                    flexShrink: 0,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <span
                                    style={{
                                        textAlign: 'center',
                                        ...typographyStyle(
                                            theme.typography.subtitle1,
                                        ),
                                        fontWeight: 700,
                                    }}
                                >
                                    Total Damage Cost:{' '}
                                    {currencyFormatter.format(damageCost)}
                                </span>
                                {loadingDamages ? (
                                    <CircularProgress />
                                ) : (
                                    <Tooltip title={'Refresh Damages'}>
                                        <IconButton
                                            onClick={() =>
                                                applyFilter(
                                                    damageFilterState,
                                                    appliedFinderSelection,
                                                    currentPage,
                                                )
                                            }
                                        >
                                            <RefreshIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}

                                <Tooltip
                                    title="Reports"
                                    style={{ marginLeft: theme.spacing(4) }}
                                >
                                    <IconButton onClick={openReportPopover}>
                                        <FileCopyIcon fontSize="large" />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip
                                    title="Analyze with Lori"
                                    style={{ marginLeft: theme.spacing(4) }}
                                >
                                    <span>
                                        <IconButton
                                            onClick={() =>
                                                setLoriModalOpen(true)
                                            }
                                            disabled={
                                                markingDuplicates ||
                                                matchingTenantIssues
                                            }
                                        >
                                            {markingDuplicates ||
                                            matchingTenantIssues ? (
                                                <CircularProgress size={28} />
                                            ) : (
                                                <LoriIcon
                                                    width={28}
                                                    height={28}
                                                />
                                            )}
                                        </IconButton>
                                    </span>
                                </Tooltip>

                                <Tooltip
                                    title="Filter"
                                    style={{ marginLeft: theme.spacing(4) }}
                                >
                                    <IconButton
                                        onClick={() =>
                                            setFilterOpen(!filterOpen)
                                        }
                                    >
                                        <TuneIcon fontSize="large" />
                                    </IconButton>
                                </Tooltip>
                            </Container>
                        </Container>

                        <Divider />
                        <Container
                            style={{
                                flex: 1,
                                flexDirection: 'column',
                            }}
                            scrollY
                        >
                            {filteredDamages?.map((damage, idx) => {
                                return (
                                    <DamageRow
                                        displayProblems={arTransactionErrorMode}
                                        key={`DAMAGE_${damage.id}`}
                                        damage={damage}
                                        selected={
                                            selectAllAcrossPages ||
                                            selectedState[idx] === true
                                        }
                                        damageConfigList={damageConfigList}
                                        messageDrawerOpen={
                                            openMessageDamageId === damage.id
                                        }
                                        onSelect={() => {
                                            if (selectAllAcrossPages) {
                                                setSelectAllAcrossPages(false)
                                                setAllSelectedDamageIds([])
                                                const next = filteredDamages.map(
                                                    () => false,
                                                )
                                                next[idx] = true
                                                setSelectedState(next)
                                                return
                                            }
                                            const newSelectedState = [
                                                ...selectedState,
                                            ]
                                            newSelectedState[
                                                idx
                                            ] = !newSelectedState[idx]
                                            setSelectedState(newSelectedState)
                                        }}
                                        handleStatusChange={
                                            handleDamageStatusChange
                                        }
                                        removeDamageTransaction={
                                            removeDamageTransaction
                                        }
                                        onOpenMessageDrawer={
                                            onOpenMessageDrawer
                                        }
                                        workspaceUser={workspaceUser}
                                        updateDamage={updateDamage}
                                        openPopover={openTenantPopover}
                                        invConfigMap={inventoryConfigMap}
                                        areaConfigMap={areaConfigMap}
                                        unitMap={unitMap}
                                        onClickDamageButton={() => {
                                            setSelectedDamage(damage)
                                            setUnitDamagesLoading(true)
                                            getDamageListForInspectionAndUnit({
                                                params: {
                                                    inspection:
                                                        damage.inspection,
                                                    unit: damage.unit,
                                                },
                                            }).then(() => {
                                                setUnitDamagesLoading(false)
                                            })
                                        }}
                                    />
                                )
                            })}
                        </Container>
                    </Container>
                    <Divider />

                    <Container
                        style={{
                            minHeight: 50,
                            justifyContent: 'center',
                            alignItems: 'center',
                            padding: theme.spacing(1),
                        }}
                    >
                        <Pagination
                            count={Math.ceil(totalDamageCount / PAGE_SIZE)}
                            page={currentPage}
                            onChange={(e, value) => {
                                setSelectAllAcrossPages(false)
                                setAllSelectedDamageIds([])
                                setPage(value)
                                getFilteredDamages(
                                    damageFilterState,
                                    value,
                                    appliedFinderSelection,
                                )
                            }}
                        />

                        <span
                            style={{
                                ...typographyStyle(theme.typography.body1),
                                fontWeight: 700,
                                marginLeft: theme.spacing(2),
                            }}
                        >
                            {(currentPage - 1) * PAGE_SIZE + 1} -{' '}
                            {currentPage * PAGE_SIZE} of {totalDamageCount}
                            {/* {totalDamageCount} Damages */}
                        </span>
                    </Container>
                </Container>

                <DamageDrawer
                    unit={unitMap[selectedDamage?.unit ?? -1]}
                    openMessages={(damage: Damage) => {
                        onOpenMessageDrawer(damage.channel, damage.id)
                    }}
                    handleClose={() => setSelectedDamage(null)}
                    areaConfigMap={areaConfigMap}
                    createDamage={createDamage}
                    inspection={selectedDamage?.inspection}
                    unitDamages={unitDamageMap[selectedDamage?.unit ?? -1]}
                    loading={unitDamagesLoading}
                />

                <MessageDrawer
                    open={openMessageDamageId > 0}
                    onClose={() => setOpenMessageDamageId(-1)}
                    onCreateMessage={(message) => {
                        setOpenMessageDamageId(-1)
                        const updatedDamage = filteredDamages?.find(
                            (dmg) => dmg.id === openMessageDamageId,
                        )

                        if (updatedDamage) {
                            updateDamageState([
                                {
                                    ...updatedDamage,
                                    message_count:
                                        updatedDamage.message_count + 1,
                                    attachments: [
                                        ...updatedDamage.attachments,
                                        ...message.attachments,
                                    ],
                                },
                            ])
                        }
                    }}
                />

                <DamageFilterDrawer
                    open={filterOpen}
                    onClose={() => setFilterOpen(false)}
                    damageFilterState={damageFilterState}
                    setDamageFilterState={setDamageFilterState}
                    tree={tree.root}
                    selection={appliedFinderSelection}
                    onApplyFilter={applyFilter}
                    inspectionList={
                        inspectionList?.filter((ins) => ins.active) ?? []
                    }
                />

                <TenantPopover
                    open={tenantPopOverOpen}
                    anchor={tenantPopoverAnchorEl}
                    onClose={() => closeTenantPopover()}
                    damage={filteredDamages?.find(
                        (dmg) => dmg.id === selectedDamageId,
                    )}
                    addDamageTransaction={addDamageTransaction}
                />

                <ScheduleReportPopOver
                    open={reportPopoverOpen}
                    onClose={closeReportPopover}
                    anchorEl={reportPopoverAnchorEl}
                    anchorOrigin={{
                        horizontal: 'left',
                        vertical: 'bottom',
                    }}
                    reportLoading={false}
                    width={455}
                    reportItems={[
                        {
                            title: 'Excel Report',
                            reportFileType: 'excel',
                            reportDelivery: 'email',
                            downloadLink: {
                                title: 'Email report',
                                onClick: () => {
                                    const params = getFilterParams(
                                        damageFilterState,
                                        appliedFinderSelection,
                                    )
                                    axiosInstance
                                        .get(
                                            'lease/damage/transaction_report/',
                                            {
                                                params: {
                                                    ...params,
                                                    include_names: true,
                                                },
                                            },
                                        )
                                        .then(() => {
                                            toast.success(
                                                'The report will be sent to your email!',
                                            )
                                        })
                                },
                            },
                        },
                        {
                            title: 'PDF Report',
                            titleSubtitle: '(With Inspector Names)',
                            reportFileType: 'pdf',
                            reportDelivery: 'email',
                            downloadLink: {
                                title: 'Email report',
                                onClick: () => {
                                    const params = getFilterParams(
                                        damageFilterState,
                                        appliedFinderSelection,
                                    )

                                    axiosInstance
                                        .get('lease/damage/bulk_pdf_report/', {
                                            params: {
                                                ...params,
                                                include_names: true,
                                            },
                                        })
                                        .then(() => {
                                            toast.success(
                                                'You Will Recieve an email when the report is done',
                                            )
                                        })
                                },
                            },
                        },
                        {
                            title: 'PDF Report',
                            titleSubtitle: '(Without Inspector Names)',
                            reportFileType: 'pdf',
                            reportDelivery: 'email',
                            downloadLink: {
                                title: 'Email report',
                                onClick: () => {
                                    const params = getFilterParams(
                                        damageFilterState,
                                        appliedFinderSelection,
                                    )

                                    axiosInstance
                                        .get('lease/damage/bulk_pdf_report/', {
                                            params: params,
                                            data: {
                                                include_names: false,
                                            },
                                        })
                                        .then(() => {
                                            toast.success(
                                                'You Will Recieve an email when the report is done',
                                            )
                                        })
                                },
                            },
                        },
                        {
                            title: 'Tenant Damage Reports',
                            reportFileType: 'pdf',
                            reportDelivery: 'email',
                            downloadLink: {
                                title: 'Email report',
                                onClick: () => {
                                    const params = getFilterParams(
                                        damageFilterState,
                                        appliedFinderSelection,
                                    )

                                    axiosInstance
                                        .get(
                                            'lease/damage/get_individual_tenant_damage_reports/',
                                            {
                                                params: params,
                                                data: {
                                                    include_names: false,
                                                },
                                            },
                                        )
                                        .then(() => {
                                            toast.success(
                                                'You will recieve an email when the report is done',
                                            )
                                        })
                                },
                            },
                        },
                    ]}
                />

                <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
                    <DialogTitle id="alert-dialog-title">
                        Reset the damage status?
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            Please confirm that you want to reset{' '}
                            {selectedCount} damages back to pending.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            color="primary"
                            variant="outlined"
                            onClick={() => setDialogOpen(false)}
                        >
                            Cancel
                        </Button>
                        <div style={{ flex: 1 }} />
                        <Button
                            color="secondary"
                            variant="outlined"
                            onClick={() => {
                                const p = handleDamageStatusChange(
                                    damagesForBulk,
                                    DamageStatus.PENDING,
                                )
                                if (p) {
                                    p.then(() => {
                                        toast.success(
                                            'Damages reset to pending.',
                                        )
                                        setDialogOpen(false)
                                    }).catch(() => {})
                                }
                            }}
                        >
                            Confirm
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={deletePastTransactionsDialogOpen}
                    onClose={() => setDeletePastTransactionsDialogOpen(false)}
                >
                    <DialogTitle id="delete-past-transactions-dialog-title">
                        Cancel Past Transactions?
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText id="delete-past-transactions-dialog-description">
                            This will cancel all AR transactions associated with
                            past leases for every pending damage. This action
                            cannot be undone.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            color="primary"
                            variant="outlined"
                            onClick={() =>
                                setDeletePastTransactionsDialogOpen(false)
                            }
                        >
                            Cancel
                        </Button>
                        <div style={{ flex: 1 }} />
                        <Button
                            color="secondary"
                            variant="outlined"
                            onClick={() => {
                                removeAllPastTransactions()
                                setDeletePastTransactionsDialogOpen(false)
                            }}
                        >
                            Confirm
                        </Button>
                    </DialogActions>
                </Dialog>

                <LoriActionsModal
                    open={loriModalOpen}
                    onClose={() => setLoriModalOpen(false)}
                    onFindDuplicates={handleMarkDuplicates}
                    onMatchTenantIssues={handleMatchTenantIssues}
                />

                <DuplicateBlanket
                    open={duplicateBlanketOpen}
                    loading={markingDuplicates}
                    results={duplicateResults}
                    workspaceUser={workspaceUser}
                    damageConfigList={damageConfigList}
                    areaConfigMap={areaConfigMap}
                    invConfigMap={inventoryConfigMap}
                    unitMap={unitMap}
                    onClose={handleCloseDuplicateBlanket}
                    onUpdateResults={setDuplicateResults}
                    handleStatusChange={handleDamageStatusChange}
                    removeDamageTransaction={removeDamageTransaction}
                    onOpenMessageDrawer={onOpenMessageDrawer}
                    updateDamage={updateDamage}
                    openPopover={openTenantPopover}
                />
            </div>
        </>
    )
}
